import { Injectable } from '@angular/core';
import type { Route } from '@angular/router';
import type { Observable } from 'rxjs';
// eslint-disable-next-line local-rules/no-lastValueFrom
import { lastValueFrom } from 'rxjs';
import type {
  PreloadAudience,
  PreloadModule,
  PreloadRouteConfig,
} from './preload-route.config';

/** 'critical' routes are warmed before the first screen, 'idle' ones after. */
export type PreloadTier = 'critical' | 'idle';

export interface EssentialRoute {
  /**
   * The audiences that need this route warmed, per tier. A route can be
   * critical for one audience and idle for another, so both lists are kept.
   */
  readonly audiences: {
    readonly [tier in PreloadTier]: readonly PreloadAudience[];
  };
  load(): Observable<any>;
}

@Injectable({
  providedIn: 'root',
})
/*
 * This allows to preload the route bundle of a lazy-loaded route, e.g. we you
 * know that the user is likely going to go somewhere else next, preloading the
 * route bundle will make the route transition faster (otherwise the route
 * bundle will just be loaded during the navigation).
 *
 * In order to use it, copy the NgModule path into a preloadModule route data
 * entry, and call PreloadRoute:preload with said preloadModule name.
 *
 * While the route itself won't be loaded, be aware that there's still a
 * (small) cost parsing the bundle & executing the wrapper, so make sure to
 * call PreloadRoute:preload only when the user is idle, e.g. waiting on an API
 * response, or it could create jitter due to the skipped frames.
 *
 * TODO: scrap this whole thing once Ivy has landed as we can just do
 * `import()` instead.
 */
export class PreloadRoute {
  routes: { [path: string]: () => void } = {};
  essentialRoutes: { [path: string]: EssentialRoute } = {};
  private loading: { [path: string]: Promise<void> } = {};

  preload(preloadModule: PreloadModule): void {
    const load = this.routes[preloadModule];
    if (load) {
      load();
    }
    // Don't log an error here as the preloadModule won't be registered if the
    // app started while on the route to preload, as the route config would
    // have already been loaded by Angular.
    // This means preloadModule typos should be detected through a lint rule,
    // not at runtime.
  }

  /**
   * Preloads every "critical" route. These are routes that we want to ensure
   * are loaded before the app is running.
   */
  preloadCriticalRoutes(audience: PreloadAudience): Promise<void> {
    return this.preloadRouteTiers(audience, ['critical']);
  }

  /**
   * Preload every "essential" route. Routes are deemed "essential" if they are
   * commonly navigated to. We do this to speed up the navigation time in app.
   * Only call this once the app is idle, as it also parses & executes the
   * bundles.
   */
  preloadEssentialRoutes(audience: PreloadAudience): Promise<void> {
    return this.preloadRouteTiers(audience, ['critical', 'idle']);
  }

  private preloadRouteTiers(
    audience: PreloadAudience,
    tiers: readonly PreloadTier[],
  ): Promise<void> {
    return Promise.all(
      Object.entries(this.essentialRoutes)
        .filter(([, essentialRoute]) =>
          tiers.some(tier => essentialRoute.audiences[tier].includes(audience)),
        )
        .map(([path, essentialRoute]) => this.loadOnce(path, essentialRoute)),
    ).then(() => undefined);
  }

  /**
   * Attempts to load a route bundle at most once, ignores errors. A bundle that
   * failed to load is dropped from the cache so a later pass can retry it.
   */
  private loadOnce(
    path: string,
    essentialRoute: EssentialRoute,
  ): Promise<void> {
    const pending = this.loading[path];
    if (pending !== undefined) {
      return pending;
    }

    const loading = lastValueFrom(essentialRoute.load(), {
      defaultValue: undefined,
    }).then(
      () => undefined,
      () => {
        delete this.loading[path];
      },
    );

    this.loading[path] = loading;

    return loading;
  }

  /**
   * @private
   * Only to be used by the OnDemandPreloadingStrategy helper service
   */
  _registerRoute(
    route: Route & { data?: PreloadRouteConfig },
    load: () => Observable<any>,
  ): void {
    if (route.data && route.data.preloadModule) {
      this.routes[route.data.preloadModule] = load;
    }

    const critical = route.data?.preloadRequired ?? [];
    const onIdle = route.data?.preloadOnIdle ?? [];
    if (route.path !== undefined && critical.length + onIdle.length > 0) {
      this.essentialRoutes[route.path] = {
        audiences: { critical, idle: onIdle },
        load,
      };
    }
  }
}
